import {
  buildThreePointAnchors,
  type AnchorMap,
  type OrderedSequence,
} from '../types';

const SIS_TERM_CODE_RE = /^(\d{4})(80)$/;
const DISPLAY_TERM_CODE_RE = /^Fall (\d{4})$/;
const LEGACY_FRIENDLY_TERM_RE = /^F(\d{2})$/;

export interface AcademicTermMember {
  readonly year: number;
  readonly season: 'Fall';
}

export function normalizeTermInput(
  input: string | null | undefined,
): string | null {
  if (typeof input !== 'string' || input.length === 0) {
    return null;
  }
  const friendly = LEGACY_FRIENDLY_TERM_RE.exec(input);
  if (friendly) {
    return `Fall 20${friendly[1]}`;
  }
  if (DISPLAY_TERM_CODE_RE.test(input)) {
    return input;
  }
  const sis = SIS_TERM_CODE_RE.exec(input);
  if (sis) {
    return `Fall ${sis[1]}`;
  }
  return null;
}

function parseCanonicalTermCode(code: string): AcademicTermMember | null {
  const display = DISPLAY_TERM_CODE_RE.exec(code);
  if (display) {
    return { year: Number(display[1]), season: 'Fall' };
  }
  const sis = SIS_TERM_CODE_RE.exec(code);
  if (sis) {
    return { year: Number(sis[1]), season: 'Fall' };
  }
  return null;
}

export function decrementTermYear(code: string): string | null {
  const canonical = normalizeTermInput(code);
  if (!canonical) {
    return null;
  }
  const parsed = parseCanonicalTermCode(canonical);
  if (!parsed) {
    return null;
  }
  return `Fall ${parsed.year - 1}`;
}

export const academicTermSequence: OrderedSequence<AcademicTermMember> = {
  id: 'term',

  parse(input: string): AcademicTermMember | null {
    const canonical = normalizeTermInput(input);
    if (!canonical) {
      return null;
    }
    return parseCanonicalTermCode(canonical);
  },

  step(member: AcademicTermMember, n: number): AcademicTermMember | null {
    if (!Number.isInteger(n)) {
      return null;
    }
    return { year: member.year + n, season: 'Fall' };
  },

  format(member: AcademicTermMember): string {
    return `${member.season} ${member.year}`;
  },

  anchors(current: string): AnchorMap | null {
    return buildThreePointAnchors(this, current);
  },
};
